import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { CheckCircle2 } from "lucide-react";

export default function SurveyResponsePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wb-surveys/respond/${token}`);
        setInfo(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

  const submit = async () => {
    const missing = info.questions.filter((q) => q.type !== "text" && !answers[q.id]);
    if (missing.length) {
      toast.error("Bitte alle Fragen bewerten, bevor du absendest");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/wb-surveys/respond/${token}`, { answers });
      setDone(true);
      toast.success("Danke für dein Feedback!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-xs text-slate-400" data-testid="survey-loading">Lädt…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 p-8 text-center" data-testid="survey-error">
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-2">Umfrage nicht verfügbar</div>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const showThankYou = info.already_submitted || done;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white border border-slate-200" data-testid="survey-response-page">
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">
            Wöchentliche Unterrichtsbewertung
          </div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">
            {showThankYou ? "Danke!" : `Hallo${info.participant_first_name ? " " + info.participant_first_name : ""}.`}
          </div>
          {!showThankYou && (
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Bitte fülle diesen Feedbackbogen aus, damit wir weiterhin an unserer stetigen Verbesserung
              arbeiten und dir qualitativ hochwertige Schulungen anbieten können. Deine Angaben werden
              selbstverständlich anonym (ohne Namen) ausgewertet; Rückschlüsse auf deine Person sind
              nicht möglich.
            </p>
          )}
        </div>
        <div className="px-8 py-6">
          {showThankYou ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center flex flex-col items-center gap-2" data-testid="survey-done">
              <CheckCircle2 size={32} strokeWidth={1.5} className="text-emerald-600" />
              <div className="font-medium text-emerald-800">Vielen Dank für dein Feedback!</div>
              <div className="text-xs text-emerald-700">Deine Antwort wurde anonym gespeichert.</div>
            </div>
          ) : (
            <div className="space-y-7">
              {info.questions.map((q, idx) => (
                <div key={q.id} data-testid={`survey-question-${q.id}`}>
                  <div className="text-sm text-slate-800 mb-2 leading-snug">{idx + 1}. {q.text}</div>
                  {q.type === "text" ? (
                    <textarea value={answers[q.id] || ""} onChange={(e) => setAnswer(q.id, e.target.value)} rows={4} maxLength={3000}
                      placeholder="Deine Antwort (optional)" data-testid={`survey-text-${q.id}`}
                      className="w-full border border-slate-300 p-3 text-sm focus:outline-none focus:border-slate-900" />
                  ) : (
                  <div className="inline-block">
                    <StarRating value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} testId={`survey-rating-${q.id}`} />
                    <div className="flex justify-between text-[11px] text-slate-500 mt-1 px-0.5">
                      <span>Bad</span>
                      <span>Good</span>
                    </div>
                  </div>
                  )}
                </div>
              ))}
              <button
                onClick={submit}
                disabled={saving || info.questions.some((q) => q.type !== "text" && !answers[q.id])}
                data-testid="survey-submit-btn"
                className="w-full bg-slate-900 text-white py-4 text-sm font-medium hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? "Sendet…" : "Absenden"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
